/**
 * Push notification registration and lifecycle management.
 *
 * On every app start (after auth) call `initPushNotifications()`.
 * It registers the service worker, checks the push subscription,
 * and (re-)sends it to the backend (idempotent upsert).
 */

import { apiService } from './api';

const SW_PATH = '/sw.js';
const E2E_PRIVATE_KEY_STORAGE = 'topbred_e2e_private_key';
const E2E_PUBLIC_KEY_STORAGE = 'topbred_e2e_public_key';

// ── IndexedDB helper (shared schema with sw.js) ─────────────────────────────

function openConfigDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('topbred_sw_config', 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore('config');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function writeSWConfig(apiUrl: string, token: string): Promise<void> {
  const db = await openConfigDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('config', 'readwrite');
    const store = tx.objectStore('config');
    store.put({ apiUrl, token }, 'sw_config');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Utilities ────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── E2E key generation ──────────────────────────────────────────────────────

async function getOrCreateE2EPublicKey(): Promise<string> {
  const existing = localStorage.getItem(E2E_PUBLIC_KEY_STORAGE);
  if (existing) return existing;

  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits'],
  );

  const publicKeyRaw = await window.crypto.subtle.exportKey('raw', keyPair.publicKey);
  const privateKeyJwk = await window.crypto.subtle.exportKey('jwk', keyPair.privateKey);

  const publicKeyB64 = arrayBufferToBase64Url(publicKeyRaw);
  localStorage.setItem(E2E_PUBLIC_KEY_STORAGE, publicKeyB64);
  localStorage.setItem(E2E_PRIVATE_KEY_STORAGE, JSON.stringify(privateKeyJwk));

  return publicKeyB64;
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function initPushNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push notifications not supported in this browser');
    return;
  }

  try {
    // 1. Fetch VAPID public key from backend
    const { public_key: vapidPublicKey } = await apiService.getVapidPublicKey();

    // 2. Register the service worker
    const registration = await navigator.serviceWorker.register(SW_PATH);
    await navigator.serviceWorker.ready;

    // 3. Write config so the SW can call the backend for receipts
    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000');
    const token = localStorage.getItem('access_token') || '';
    await writeSWConfig(apiUrl, token);

    // 4. Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Request permission and subscribe
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return;
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    // 5. Generate E2E client key pair (stored locally)
    const clientPublicKey = await getOrCreateE2EPublicKey();

    // 6. Send subscription to backend (idempotent upsert)
    const subJson = subscription.toJSON();
    await apiService.subscribePush({
      endpoint: subscription.endpoint,
      p256dh_key: subJson.keys?.p256dh || '',
      auth_key: subJson.keys?.auth || '',
      user_agent: navigator.userAgent,
      client_public_key: clientPublicKey,
    });
  } catch (err) {
    console.error('Failed to initialize push notifications:', err);
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Notify backend first
      await apiService.unsubscribePush({ endpoint: subscription.endpoint });
      // Then unsubscribe from the push service
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('Failed to unsubscribe from push:', err);
  }
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}
